import { Add, Delete } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import TimeInputWithBlur from '../../components/TimeInputWithBlur';
import { sortPrintersByName } from '../../helpers/printers';
import {
    buildMaintenancePayload,
    DEFAULT_MAINTENANCE_DURATION,
    durationToMinutes,
    maintenanceFromApi,
    MAINTENANCE_TYPES,
    numericInputProps,
    partsToRows,
} from '../../helpers/printerMaintenances';

function EditPrinterMaintenancePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [printers, setPrinters] = useState([]);
    const [form, setForm] = useState({
        printer_uuid: '',
        occurred_at: '',
        type: 'manutencao_preventiva',
        problem_description: '',
        analysis_duration: DEFAULT_MAINTENANCE_DURATION,
        performed_actions: '',
        printer_hours_count: '',
        correction_duration: DEFAULT_MAINTENANCE_DURATION,
    });
    const [replacementParts, setReplacementParts] = useState(['']);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });

    useEffect(() => {
        Promise.all([
            api.get('printers?page=1&limit=100'),
            api.get(`printer-maintenances/${id}`),
        ])
            .then(([printersRes, maintenanceRes]) => {
                setPrinters(sortPrintersByName(printersRes.data.printers || []));
                const data = maintenanceRes.data.data || {};
                const parsed = maintenanceFromApi(data);
                setForm({
                    ...parsed,
                    printer_hours_count: String(parsed.printer_hours_count),
                });
                setReplacementParts(partsToRows(data.replacement_parts));
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const updateForm = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handlePartChange = (index, value) => {
        setReplacementParts((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const addPartRow = () => setReplacementParts((prev) => [...prev, '']);

    const removePartRow = (index) => {
        setReplacementParts((prev) => (prev.length <= 1 ? [''] : prev.filter((_, i) => i !== index)));
    };

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const nextErrors = { has_errors: false };
        if (!form.printer_uuid) {
            nextErrors.printer_uuid = 'Selecione a impressora.';
        }
        if (!form.problem_description.trim()) {
            nextErrors.problem_description = 'Informe a descrição do problema.';
        }
        if (form.analysis_duration.replace(/\D/g, '') && durationToMinutes(form.analysis_duration) === null) {
            nextErrors.analysis_duration = 'Minutos inválidos (use até 59).';
        }
        if (form.correction_duration.replace(/\D/g, '') && durationToMinutes(form.correction_duration) === null) {
            nextErrors.correction_duration = 'Minutos inválidos (use até 59).';
        }

        if (Object.keys(nextErrors).length > 1) {
            nextErrors.has_errors = true;
            setResultErrors(nextErrors);
            return;
        }

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        api.put(`printer-maintenances/${id}`, buildMaintenancePayload(form, replacementParts))
            .then(() => {
                navigate('/admin/printer-maintenances');
            })
            .catch((error) => {
                const data = error.response?.data || {};
                setResultErrors({
                    ...(data.errors || {}),
                    has_errors: true,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/printer-maintenances')} to="/admin/printer-maintenances">
                    Manutenções
                </Link>
                <Typography color="text.primary">Editando manutenção</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Manutenção</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="printer-label">Impressora</InputLabel>
                            <Select
                                labelId="printer-label"
                                label="Impressora"
                                value={form.printer_uuid}
                                onChange={(event) => updateForm('printer_uuid', event.target.value)}
                            >
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('printer_uuid')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Data da manutenção"
                            fullWidth
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={form.occurred_at}
                            onChange={(event) => updateForm('occurred_at', event.target.value)}
                        />
                        {renderFieldError('occurred_at')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="type-label">Tipo</InputLabel>
                            <Select
                                labelId="type-label"
                                label="Tipo"
                                value={form.type}
                                onChange={(event) => updateForm('type', event.target.value)}
                            >
                                {MAINTENANCE_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('type')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Contagem de horas da impressora"
                            fullWidth
                            type="number"
                            inputProps={numericInputProps(0)}
                            value={form.printer_hours_count}
                            onChange={(event) => updateForm('printer_hours_count', event.target.value)}
                        />
                        {renderFieldError('printer_hours_count')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Descrição do problema"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.problem_description}
                            onChange={(event) => updateForm('problem_description', event.target.value)}
                        />
                        {renderFieldError('problem_description')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TimeInputWithBlur
                            label="Tempo de análise"
                            value={form.analysis_duration}
                            onChange={(event) => updateForm('analysis_duration', event.target.value)}
                        />
                        {renderFieldError('analysis_duration')}
                        {renderFieldError('analysis_duration_minutes')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TimeInputWithBlur
                            label="Tempo de correção"
                            value={form.correction_duration}
                            onChange={(event) => updateForm('correction_duration', event.target.value)}
                        />
                        {renderFieldError('correction_duration')}
                        {renderFieldError('correction_duration_minutes')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="O que foi feito"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.performed_actions}
                            onChange={(event) => updateForm('performed_actions', event.target.value)}
                        />
                        {renderFieldError('performed_actions')}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Troca de peça
                        </Typography>
                        {replacementParts.map((part, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <TextField
                                    label={`Peça ${index + 1}`}
                                    fullWidth
                                    value={part}
                                    onChange={(event) => handlePartChange(index, event.target.value)}
                                />
                                <IconButton color="error" onClick={() => removePartRow(index)} aria-label="remover peça">
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<Add />} onClick={addPartRow} type="button">
                            Adicionar peça
                        </Button>
                        {renderFieldError('replacement_parts')}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Atualizando...' : 'Atualizar Manutenção'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default EditPrinterMaintenancePage;
